import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Modal,
  TextInput,
  useWindowDimensions,
  Platform,
} from 'react-native';
import { LoadingSpinner } from '../common/LoadingSpinner';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useAuth } from '../../context/AuthContext';
import { feeAPI } from '../../services/api';
import { Colors, FontSize, Spacing, BorderRadius } from '../../theme/colors';
import { FeeStructureOption, PaymentFrequency } from '../../types';
import { Logger } from '../../utils/logger';

type Step = 'congrats' | 'installment';

/** One match from GET /SiblingDiscount/sibling-search - name and class only. */
interface SiblingSuggestion {
  name: string;
  classId: string;
  className?: string | null;
}

interface Props {
  visible: boolean;
  onSaved: () => void;
}

// Enterprise-grade modal shown over the Home screen when enrollment has no
// fee/installment plan selected yet. Has a persistent header and footer, rich plan
// comparison cards, and a clean sibling discount section.
export function FeeOnboardingModal({ visible, onSaved }: Props) {
  const { selectedStudent } = useAuth();
  const { width } = useWindowDimensions();
  const isMobile = width < 600;
  const isSmallMobile = width < 420;

  const [step, setStep] = useState<Step>('congrats');
  const [selectedFrequency, setSelectedFrequency] = useState<PaymentFrequency>('Yearly');
  const [feeData, setFeeData] = useState<FeeStructureOption | null>(null);
  const [loadingFees, setLoadingFees] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sibling discount declaration
  const [hasSibling, setHasSibling] = useState<boolean | null>(null);
  const [siblingCount, setSiblingCount] = useState<number>(2);
  const [siblingNames, setSiblingNames] = useState<string[]>(['']);
  const siblingInputRefs = useRef<Array<TextInput | null>>([]);

  const [siblingClassIds, setSiblingClassIds] = useState<Array<string | null>>([null]);
  const [activeSiblingIndex, setActiveSiblingIndex] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<SiblingSuggestion[]>([]);
  const [searchingSiblings, setSearchingSiblings] = useState(false);

  const activeQuery = activeSiblingIndex == null ? '' : (siblingNames[activeSiblingIndex] ?? '').trim();
  const activePicked = activeSiblingIndex != null && !!siblingClassIds[activeSiblingIndex];

  useEffect(() => {
    if (!selectedStudent?.id || activeQuery.length < 3 || activePicked) {
      setSuggestions([]);
      setSearchingSiblings(false);
      return;
    }
    let cancelled = false;
    setSearchingSiblings(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await feeAPI.searchSiblings(selectedStudent.id, activeQuery);
        if (!cancelled) setSuggestions(Array.isArray(data) ? (data as SiblingSuggestion[]) : []);
      } catch (err) {
        Logger.error('Onboarding', 'Sibling search failed', err);
        if (!cancelled) setSuggestions([]);
      } finally {
        if (!cancelled) setSearchingSiblings(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [selectedStudent?.id, activeQuery, activePicked]);

  const pickSuggestion = (index: number, s: SiblingSuggestion) => {
    setSiblingNames(prev => prev.map((n, i) => (i === index ? s.name : n)));
    setSiblingClassIds(prev => prev.map((c, i) => (i === index ? s.classId : c)));
    setSuggestions([]);
    siblingInputRefs.current[index + 1]?.focus();
  };

  useEffect(() => {
    if (visible) setStep('congrats');
  }, [visible]);

  useEffect(() => {
    if (!visible || step !== 'installment' || !selectedStudent?.classId || !selectedStudent?.academicYear) return;
    let cancelled = false;
    (async () => {
      setLoadingFees(true);
      try {
        const { data } = await feeAPI.getClassFeeStructures(selectedStudent.classId, selectedStudent.academicYear);
        if (!cancelled) setFeeData((data as any) ?? null);
      } catch (err) {
        Logger.error('Onboarding', 'Failed to load fee structures', err);
        if (!cancelled) Alert.alert('Error', 'Failed to load fee structures. Please try again.');
      } finally {
        if (!cancelled) setLoadingFees(false);
      }
    })();
    return () => { cancelled = true; };
  }, [visible, step, selectedStudent?.classId, selectedStudent?.academicYear]);

  const yearlyFee = feeData?.yearly ?? 0;
  const quarterlyFee = feeData?.termly ?? 0;
  const monthlyFee = feeData?.monthly ?? 0;
  const hasFeeData = !!feeData && (yearlyFee > 0 || quarterlyFee > 0 || monthlyFee > 0);

  useEffect(() => {
    if (hasSibling !== true) return;
    setSiblingNames(prev => {
      const wanted = Math.max(1, siblingCount - 1);
      if (prev.length === wanted) return prev;
      return Array.from({ length: wanted }, (_, i) => prev[i] ?? '');
    });
    setSiblingClassIds(prev => {
      const wanted = Math.max(1, siblingCount - 1);
      if (prev.length === wanted) return prev;
      return Array.from({ length: wanted }, (_, i) => prev[i] ?? null);
    });
  }, [hasSibling, siblingCount]);

  const selectedAmount = useMemo(() => {
    if (selectedFrequency === 'Yearly') return yearlyFee;
    if (selectedFrequency === 'Termly') return quarterlyFee;
    return monthlyFee;
  }, [selectedFrequency, yearlyFee, quarterlyFee, monthlyFee]);

  const formatCurrency = (amt: number) => {
    return amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const handleSaveInstallment = async () => {
    if (!feeData || !selectedStudent?.enrollmentId) return;
    setSaving(true);
    const amount =
      selectedFrequency === 'Yearly' ? yearlyFee : selectedFrequency === 'Termly' ? quarterlyFee : monthlyFee;
    try {
      await feeAPI.updateFeeSelection(selectedStudent.enrollmentId, {
        academicFeeStructureId: feeData.id,
        paymentFrequency: selectedFrequency,
        amount,
      });

      if (hasSibling !== null && selectedStudent?.id) {
        try {
          await feeAPI.declareSiblings(selectedStudent.id, {
            hasSibling,
            siblingCount: hasSibling ? siblingCount : null,
            paymentFrequency: selectedFrequency,
            academicYear:
              selectedStudent?.academicYear == null
                ? null
                : String(selectedStudent.academicYear),
            siblings: hasSibling
              ? siblingNames
                  .map((n, i) => ({ name: n.trim(), classId: siblingClassIds[i] ?? null }))
                  .filter(s => s.name)
              : [],
          });
        } catch (siblingErr) {
          Logger.error('Onboarding', 'Failed to record sibling declaration', siblingErr);
        }
      }

      onSaved();
    } catch (err) {
      Logger.error('Onboarding', 'Failed to save fee selection', err);
      Alert.alert('Error', 'Failed to save your installment selection. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const planOptions = [
    {
      key: 'Yearly' as PaymentFrequency,
      label: 'Annual Plan',
      badge: 'Best Value',
      badgeColor: '#059669',
      badgeBg: '#ECFDF5',
      badgeBorder: '#A7F3D0',
      desc: 'Pay once for the full academic year',
      amount: yearlyFee,
      unit: '/ year',
    },
    {
      key: 'Termly' as PaymentFrequency,
      label: 'Quarterly',
      badge: 'Popular',
      badgeColor: '#0284C7',
      badgeBg: '#F0F9FF',
      badgeBorder: '#BAE6FD',
      desc: 'Pay at the start of each academic term',
      amount: quarterlyFee,
      unit: '/ term',
    },
    {
      key: 'Monthly' as PaymentFrequency,
      label: 'Monthly',
      badge: 'Flexible',
      badgeColor: '#4F46E5',
      badgeBg: '#EEF2FF',
      badgeBorder: '#C7D2FE',
      desc: 'Spread payments across monthly installments',
      amount: monthlyFee,
      unit: '/ month',
    },
  ];

  const studentDisplayName = selectedStudent?.name ?? 'Your child';
  const studentClass = selectedStudent?.class ?? 'Kindergarten 2';
  const studentYear = selectedStudent?.academicYear ? `${selectedStudent.academicYear}-${String(selectedStudent.academicYear + 1).slice(-2)}` : '2026-27';

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={() => {}} statusBarTranslucent>
      <View style={styles.overlay}>
        <View style={[styles.card, isMobile && styles.cardMobile]}>
          {/* Top Brand Accent Stripe */}
          <View style={styles.topAccent} />

          {step === 'congrats' ? (
            /* CONGRATULATIONS ONBOARDING WELCOME STEP (PROPER ENTERPRISE DESIGN WITHOUT ENROLLED WORDS) */
            <View style={styles.congratsContainer}>
              <View style={styles.congratsBadgeGlow}>
                <View style={styles.congratsIconWrapper}>
                  <FeatherIcon name="check-circle" size={44} color={Colors.success} />
                </View>
              </View>

              <View style={styles.congratsPill}>
                <Text style={styles.congratsPillText}>🎓 Onboarding Confirmed</Text>
              </View>

              <Text style={styles.congratsTitle}>Welcome to DigitalCampus!</Text>
              <Text style={styles.congratsBody}>
                <Text style={styles.congratsBodyBold}>{studentDisplayName}</Text> has been successfully onboarded for{' '}
                <Text style={styles.congratsBodyBold}>{studentClass}</Text> ({studentYear}). Please select a fee installment plan to continue.
              </Text>

              {/* Student Summary Card */}
              <View style={styles.congratsSummaryCard}>
                <View style={styles.congratsSummaryRow}>
                  <View style={styles.congratsSummaryCol}>
                    <Text style={styles.congratsSummaryLabel}>STUDENT</Text>
                    <Text style={styles.congratsSummaryValue} numberOfLines={1}>{studentDisplayName}</Text>
                  </View>
                  <View style={styles.congratsSummaryCol}>
                    <Text style={styles.congratsSummaryLabel}>CLASS & GRADE</Text>
                    <Text style={styles.congratsSummaryValue} numberOfLines={1}>{studentClass}</Text>
                  </View>
                  <View style={styles.congratsSummaryCol}>
                    <Text style={styles.congratsSummaryLabel}>ACADEMIC SESSION</Text>
                    <Text style={styles.congratsSummaryValue}>{studentYear}</Text>
                  </View>
                </View>
              </View>

              <TouchableOpacity
                style={styles.primaryButton}
                onPress={() => setStep('installment')}
                activeOpacity={0.88}
                accessibilityRole="button"
                accessibilityLabel="Select Installment Plan"
              >
                <Text style={styles.primaryButtonText}>Select Installment Plan</Text>
              </TouchableOpacity>
            </View>
          ) : (
            /* INSTALLMENT PLAN SELECTION STEP - ENTERPRISE & CLEAN UI/UX */
            <View style={styles.modalLayout}>
              {/* PINNED HEADER */}
              <View style={styles.pinnedHeader}>
                <View style={styles.headerTopRow}>
                  <View style={styles.headerTitleWrap}>
                    <View style={styles.headerIconCircle}>
                      <FeatherIcon name="credit-card" size={20} color={Colors.primary} />
                    </View>
                    <View style={styles.headerTitleTexts}>
                      <Text style={styles.headerMainTitle}>Select Tuition Payment Plan</Text>
                      <Text style={styles.headerSubtitle}>
                        Choose a payment schedule for <Text style={{ fontWeight: '700', color: Colors.text }}>{studentDisplayName}</Text>
                      </Text>
                    </View>
                  </View>

                  <View style={styles.studentBadgePill}>
                    <FeatherIcon name="user" size={13} color={Colors.primary} style={{ marginRight: 5 }} />
                    <Text style={styles.studentBadgeText} numberOfLines={1}>{studentClass}</Text>
                  </View>
                </View>
              </View>

              {/* SCROLLABLE CONTENT BODY */}
              <ScrollView
                style={styles.scrollArea}
                contentContainerStyle={styles.installmentContent}
                showsVerticalScrollIndicator={true}
              >
                {loadingFees ? (
                  <LoadingSpinner message="Loading the class fee plans..." />
                ) : !hasFeeData ? (
                  <View style={styles.emptyState}>
                    <View style={styles.emptyIconCircle}>
                      <FeatherIcon name="alert-circle" size={32} color={Colors.warning} />
                    </View>
                    <Text style={styles.emptyStateTitle}>Fee Structure Not Found</Text>
                    <Text style={styles.emptyStateText}>
                      No fee schedule is currently configured for this class and academic year. Please contact the school office.
                    </Text>
                  </View>
                ) : (
                  <>
                    <View style={styles.sectionHeaderWrap}>
                      <Text style={styles.sectionEyebrow}>PAYMENT FREQUENCY OPTIONS</Text>
                      <Text style={styles.sectionHelpText}>Select one plan to apply to this academic year's invoices:</Text>
                    </View>

                    {/* Compact, Simple Plan Cards */}
                    <View style={styles.optionsList}>
                      {planOptions.map((opt) => {
                        const isSelected = selectedFrequency === opt.key;
                        return (
                          <TouchableOpacity
                            key={opt.key}
                            style={[
                              styles.planCard,
                              isSelected && styles.planCardSelected,
                            ]}
                            onPress={() => setSelectedFrequency(opt.key)}
                            disabled={saving}
                            activeOpacity={0.85}
                            accessibilityRole="radio"
                            accessibilityState={{ selected: isSelected }}
                          >
                            <View style={styles.planCardLeft}>
                              {/* Selection Radio */}
                              <View style={[styles.customRadio, isSelected && styles.customRadioSelected]}>
                                {isSelected ? (
                                  <FeatherIcon name="check" size={12} color="#FFFFFF" />
                                ) : null}
                              </View>

                              {/* Title, Badge & Subtitle */}
                              <View style={styles.planCardInfo}>
                                <View style={styles.planCardTitleRow}>
                                  <Text style={[styles.planCardTitle, isSelected && styles.planCardTitleSelected]}>
                                    {opt.label}
                                  </Text>
                                  {opt.badge ? (
                                    <View
                                      style={[
                                        styles.planBadge,
                                        { backgroundColor: opt.badgeBg, borderColor: opt.badgeBorder },
                                      ]}
                                    >
                                      <Text style={[styles.planBadgeText, { color: opt.badgeColor }]}>{opt.badge}</Text>
                                    </View>
                                  ) : null}
                                </View>
                                <Text style={styles.planCardDesc} numberOfLines={1}>
                                  {opt.desc}
                                </Text>
                              </View>
                            </View>

                            {/* Price & Billing Unit */}
                            <View style={styles.planCardRight}>
                              <Text style={[styles.priceAmount, isSelected && styles.priceAmountSelected]}>
                                ${formatCurrency(opt.amount)}
                              </Text>
                              <Text style={styles.priceUnit}>{opt.unit}</Text>
                            </View>
                          </TouchableOpacity>
                        );
                      })}
                    </View>

                    {/* SIBLING & MULTI-CHILD DISCOUNT CARD */}
                    <View style={styles.siblingCard}>
                      <View style={styles.siblingHeaderRow}>
                        <View style={styles.siblingIconBox}>
                          <FeatherIcon name="users" size={18} color="#4F46E5" />
                        </View>
                        <View style={{ flex: 1 }}>
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                            <Text style={styles.siblingCardTitle}>Sibling & Multi-Child Discount</Text>
                            <View style={styles.optionalTag}>
                              <Text style={styles.optionalTagText}>Optional</Text>
                            </View>
                          </View>
                          <Text style={styles.siblingCardSubtitle}>
                            Families with multiple children at this school may be eligible for a concession.
                          </Text>
                        </View>
                      </View>

                      <View style={styles.siblingQuestionBlock}>
                        <Text style={styles.siblingQuestion}>
                          Do you have other children attending this school?
                        </Text>

                        {/* Segmented Yes / No Toggle */}
                        <View style={styles.segmentedToggle}>
                          <TouchableOpacity
                            style={[
                              styles.segmentedBtn,
                              hasSibling === true && styles.segmentedBtnActive,
                            ]}
                            onPress={() => setHasSibling(true)}
                            disabled={saving}
                            activeOpacity={0.85}
                          >
                            <FeatherIcon
                              name="check"
                              size={15}
                              color={hasSibling === true ? Colors.primary : Colors.textSecondary}
                              style={{ marginRight: 6 }}
                            />
                            <Text
                              style={[
                                styles.segmentedBtnText,
                                hasSibling === true && styles.segmentedBtnTextActive,
                              ]}
                            >
                              Yes, have other children
                            </Text>
                          </TouchableOpacity>

                          <TouchableOpacity
                            style={[
                              styles.segmentedBtn,
                              hasSibling === false && styles.segmentedBtnActive,
                            ]}
                            onPress={() => setHasSibling(false)}
                            disabled={saving}
                            activeOpacity={0.85}
                          >
                            <Text
                              style={[
                                styles.segmentedBtnText,
                                hasSibling === false && styles.segmentedBtnTextActive,
                              ]}
                            >
                              No, only {studentDisplayName}
                            </Text>
                          </TouchableOpacity>
                        </View>
                      </View>

                      {hasSibling === true && (
                        <View style={styles.siblingExpandedSection}>
                          {/* Sibling Count Picker */}
                          <View style={styles.siblingCountWrapper}>
                            <Text style={styles.siblingFieldLabel}>
                              Total children attending here in total (including {studentDisplayName}):
                            </Text>
                            <View style={styles.countChoicesRow}>
                              {[2, 3, 4, 5].map((count) => {
                                const isCountSelected = siblingCount === count;
                                return (
                                  <TouchableOpacity
                                    key={count}
                                    style={[
                                      styles.countPill,
                                      isCountSelected && styles.countPillSelected,
                                    ]}
                                    onPress={() => setSiblingCount(count)}
                                    disabled={saving}
                                    activeOpacity={0.85}
                                  >
                                    <Text
                                      style={[
                                        styles.countPillText,
                                        isCountSelected && styles.countPillTextSelected,
                                      ]}
                                    >
                                      {count === 5 ? '5+ Children' : `${count} Children`}
                                    </Text>
                                  </TouchableOpacity>
                                );
                              })}
                            </View>
                          </View>

                          {/* Sibling Names Input Fields */}
                          <View style={styles.siblingNamesList}>
                            <Text style={styles.siblingFieldLabel}>
                              What are their names? (Search school records)
                            </Text>

                            {siblingNames.map((name, index) => {
                              const isPicked = !!siblingClassIds[index];
                              return (
                                <View
                                  key={index}
                                  style={[
                                    styles.siblingInputCard,
                                    { zIndex: activeSiblingIndex === index ? 1000 : (100 - index) },
                                  ]}
                                >
                                  <Text style={styles.siblingInputCardTitle}>
                                    Child #{index + 2}'s Full Name
                                  </Text>
                                  <View
                                    style={[
                                      styles.siblingInputWrapper,
                                      activeSiblingIndex === index && styles.siblingInputWrapperFocused,
                                      isPicked && styles.siblingInputWrapperPicked,
                                    ]}
                                  >
                                    <FeatherIcon
                                      name="user"
                                      size={16}
                                      color={isPicked ? Colors.success : activeSiblingIndex === index ? Colors.primary : Colors.textLight}
                                      style={styles.siblingInputIcon}
                                    />
                                    <TextInput
                                      ref={(el) => { siblingInputRefs.current[index] = el; }}
                                      style={[
                                        styles.siblingInput,
                                        Platform.OS === 'web' && ({ outlineStyle: 'none', outline: 'none', boxShadow: 'none' } as any),
                                      ]}
                                      underlineColorAndroid="transparent"
                                      value={name}
                                      onChangeText={(text) => {
                                        setSiblingNames((prev) =>
                                          prev.map((n, i) => (i === index ? text : n))
                                        );
                                        setSiblingClassIds((prev) =>
                                          prev.map((c, i) => (i === index ? null : c))
                                        );
                                      }}
                                      onFocus={() => setActiveSiblingIndex(index)}
                                      placeholder="Start typing name (e.g. Jordan Smith)..."
                                      placeholderTextColor={Colors.textLight}
                                      editable={!saving}
                                      autoCapitalize="words"
                                      autoCorrect={false}
                                      maxLength={100}
                                      returnKeyType={index === siblingNames.length - 1 ? 'done' : 'next'}
                                      onSubmitEditing={() => siblingInputRefs.current[index + 1]?.focus()}
                                      blurOnSubmit={index === siblingNames.length - 1}
                                    />
                                    {isPicked ? (
                                      <View style={styles.verifiedBadge}>
                                        <FeatherIcon name="check-circle" size={14} color={Colors.success} style={{ marginRight: 4 }} />
                                        <Text style={styles.verifiedBadgeText}>Matched</Text>
                                      </View>
                                    ) : null}
                                  </View>

                                  {/* Auto-suggest dropdown */}
                                  {activeSiblingIndex === index && !siblingClassIds[index] && name.trim().length >= 3 ? (
                                    <View style={styles.suggestionList}>
                                      {searchingSiblings ? (
                                        <View style={styles.suggestionStatus}>
                                          <ActivityIndicator size="small" color={Colors.primary} />
                                          <Text style={styles.suggestionStatusText}>Searching school roster…</Text>
                                        </View>
                                      ) : suggestions.length > 0 ? (
                                        suggestions.map((s, i) => (
                                          <TouchableOpacity
                                            key={`${s.name}-${s.classId}-${i}`}
                                            style={[
                                              styles.suggestionRow,
                                              i < suggestions.length - 1 && styles.suggestionRowDivider,
                                            ]}
                                            onPress={() => pickSuggestion(index, s)}
                                            activeOpacity={0.8}
                                            accessibilityRole="button"
                                            accessibilityLabel={`${s.name}${s.className ? `, ${s.className}` : ''}`}
                                          >
                                            <View style={styles.suggestionIconBox}>
                                              <FeatherIcon name="user-check" size={14} color={Colors.primary} />
                                            </View>
                                            <View style={{ flex: 1 }}>
                                              <Text style={styles.suggestionName} numberOfLines={1}>{s.name}</Text>
                                              {s.className ? (
                                                <Text style={styles.suggestionClass} numberOfLines={1}>Class: {s.className}</Text>
                                              ) : null}
                                            </View>
                                          </TouchableOpacity>
                                        ))
                                      ) : (
                                        <View style={styles.suggestionStatus}>
                                          <Text style={styles.suggestionStatusText}>
                                            No match found. Type the full name — the office will review it manually.
                                          </Text>
                                        </View>
                                      )}
                                    </View>
                                  ) : null}
                                </View>
                              );
                            })}
                          </View>

                          {/* Enterprise Verification Notice Box */}
                          <View style={styles.noticeBox}>
                            <FeatherIcon name="info" size={16} color={Colors.primary} style={{ marginTop: 2, marginRight: 8 }} />
                            <Text style={styles.noticeText}>
                              <Text style={{ fontWeight: '700' }}>School Office Verification:</Text> Multi-child discounts are validated against official records. Any entitled deduction will be adjusted directly on your invoices by administration.
                            </Text>
                          </View>
                        </View>
                      )}
                    </View>
                  </>
                )}
              </ScrollView>

              {/* PINNED STICKY FOOTER - Summary + CTA */}
              <View style={styles.pinnedFooter}>
                <View style={styles.footerInner}>
                  <View style={styles.footerSummary}>
                    <Text style={styles.footerSummaryLabel}>SELECTED PAYMENT SCHEDULE</Text>
                    <View style={styles.footerSummaryPriceRow}>
                      <Text style={styles.footerSummaryPlanName}>
                        {selectedFrequency === 'Yearly' ? 'Annual Plan' : selectedFrequency === 'Termly' ? 'Quarterly' : 'Monthly'}
                      </Text>
                      <Text style={styles.footerSummaryDivider}>•</Text>
                      <Text style={styles.footerSummaryAmount}>
                        ${formatCurrency(selectedAmount)}
                      </Text>
                      <Text style={styles.footerSummaryUnit}>
                        {selectedFrequency === 'Yearly' ? '/ year' : selectedFrequency === 'Termly' ? '/ term' : '/ mo'}
                      </Text>
                    </View>
                  </View>

                  <TouchableOpacity
                    style={[
                      styles.primaryButton,
                      styles.footerButton,
                      (!hasFeeData || saving) && styles.primaryButtonDisabled,
                    ]}
                    onPress={handleSaveInstallment}
                    disabled={!hasFeeData || saving}
                    activeOpacity={0.88}
                  >
                    {saving ? (
                      <View style={styles.buttonLoadingRow}>
                        <ActivityIndicator size="small" color="#FFFFFF" style={{ marginRight: 8 }} />
                        <Text style={styles.primaryButtonText}>Confirming Plan…</Text>
                      </View>
                    ) : (
                      <Text style={styles.primaryButtonText}>Save &amp; Continue</Text>
                    )}
                  </TouchableOpacity>
                </View>

                {/* Micro Security Trust Mark */}
                <View style={styles.trustMarkRow}>
                  <FeatherIcon name="lock" size={12} color={Colors.textLight} style={{ marginRight: 4 }} />
                  <Text style={styles.trustMarkText}>
                    Secure school record · Powered by DigitalCampus
                  </Text>
                </View>
              </View>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(15, 23, 42, 0.68)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.md,
    ...(Platform.OS === 'web'
      ? ({
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        } as any)
      : {}),
  },
  card: {
    width: '100%',
    maxWidth: 620,
    maxHeight: '90%',
    backgroundColor: '#FFFFFF',
    borderRadius: 24,
    overflow: 'hidden',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 16 },
    shadowOpacity: 0.28,
    shadowRadius: 36,
    elevation: 12,
    borderWidth: 1,
    borderColor: 'rgba(226, 232, 240, 0.85)',
  },
  cardMobile: {
    maxWidth: '96%',
    maxHeight: '94%',
    borderRadius: 20,
  },
  topAccent: {
    height: 4,
    backgroundColor: Colors.primary,
    width: '100%',
  },
  modalLayout: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
  },

  /* PINNED HEADER */
  pinnedHeader: {
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  headerTopRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  headerTitleWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    flex: 1,
  },
  headerIconCircle: {
    width: 44,
    height: 44,
    borderRadius: 14,
    backgroundColor: '#EFF6FF',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#DBEAFE',
  },
  headerTitleTexts: {
    flex: 1,
  },
  headerMainTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#0F172A',
    letterSpacing: -0.3,
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#64748B',
    marginTop: 2,
    lineHeight: 18,
  },
  studentBadgePill: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 16,
    flexShrink: 0,
  },
  studentBadgeText: {
    fontSize: 12,
    fontWeight: '700',
    color: Colors.primary,
  },

  /* SCROLLABLE CONTENT BODY */
  scrollArea: {
    flex: 1,
  },
  installmentContent: {
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  sectionHeaderWrap: {
    marginBottom: 16,
  },
  sectionEyebrow: {
    fontSize: 11,
    fontWeight: '800',
    color: '#64748B',
    letterSpacing: 0.8,
  },
  sectionHelpText: {
    fontSize: 13,
    color: '#334155',
    marginTop: 3,
  },

  /* PLAN CARDS - COMPACT & SIMPLE */
  optionsList: {
    gap: 10,
    marginBottom: 18,
  },
  planCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderWidth: 1.5,
    borderColor: '#E2E8F0',
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 14,
    transitionDuration: '0.2s',
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  planCardSelected: {
    borderColor: Colors.primary,
    backgroundColor: '#F8FAFF',
    shadowColor: Colors.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 2,
  },
  planCardLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    marginRight: 12,
  },
  customRadio: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#CBD5E1',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  customRadioSelected: {
    borderColor: Colors.primary,
    backgroundColor: Colors.primary,
  },
  planCardInfo: {
    flex: 1,
  },
  planCardTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 2,
  },
  planCardTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#0F172A',
  },
  planCardTitleSelected: {
    color: Colors.primaryDark,
  },
  planBadge: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 10,
    borderWidth: 1,
  },
  planBadgeText: {
    fontSize: 10,
    fontWeight: '700',
  },
  planCardDesc: {
    fontSize: 12,
    color: '#64748B',
    lineHeight: 16,
  },
  planCardRight: {
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  priceAmount: {
    fontSize: 16,
    fontWeight: '800',
    color: '#0F172A',
    letterSpacing: -0.3,
  },
  priceAmountSelected: {
    color: Colors.primary,
  },
  priceUnit: {
    fontSize: 11,
    fontWeight: '600',
    color: '#64748B',
    marginTop: 1,
  },

  /* SIBLING & MULTI-CHILD CARD */
  siblingCard: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 20,
    padding: 18,
    marginBottom: 10,
  },
  siblingHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 16,
  },
  siblingIconBox: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: '#EEF2FF',
    borderWidth: 1,
    borderColor: '#E0E7FF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  siblingCardTitle: {
    fontSize: 15,
    fontWeight: '800',
    color: '#0F172A',
  },
  siblingCardSubtitle: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 2,
    lineHeight: 16,
  },
  optionalTag: {
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 6,
  },
  optionalTagText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#64748B',
    textTransform: 'uppercase',
  },
  siblingQuestionBlock: {
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    paddingTop: 14,
  },
  siblingQuestion: {
    fontSize: 14,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 10,
  },
  segmentedToggle: {
    flexDirection: 'row',
    gap: 10,
  },
  segmentedBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1.5,
    borderColor: '#CBD5E1',
    borderRadius: 14,
    paddingVertical: 11,
    paddingHorizontal: 12,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  segmentedBtnActive: {
    borderColor: Colors.primary,
    backgroundColor: '#EFF6FF',
  },
  segmentedBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#475569',
  },
  segmentedBtnTextActive: {
    color: Colors.primary,
    fontWeight: '800',
  },
  siblingExpandedSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    position: 'relative',
    zIndex: 10,
  },
  siblingCountWrapper: {
    marginBottom: 16,
  },
  siblingFieldLabel: {
    fontSize: 13,
    fontWeight: '700',
    color: '#334155',
    marginBottom: 8,
  },
  countChoicesRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  countPill: {
    flex: 1,
    minWidth: 70,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#CBD5E1',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  countPillSelected: {
    backgroundColor: Colors.primary,
    borderColor: Colors.primary,
  },
  countPillText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#334155',
  },
  countPillTextSelected: {
    color: '#FFFFFF',
    fontWeight: '800',
  },
  siblingNamesList: {
    gap: 12,
    marginBottom: 14,
    position: 'relative',
    zIndex: 50,
  },
  siblingInputCard: {
    position: 'relative',
  },
  siblingInputCardTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#64748B',
    marginBottom: 4,
  },
  siblingInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1.5,
    borderColor: '#CBD5E1',
    borderRadius: 14,
    paddingHorizontal: 12,
    height: 44,
  },
  siblingInputWrapperFocused: {
    borderColor: Colors.primary,
    backgroundColor: '#FFFFFF',
    shadowColor: Colors.primary,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 6,
    elevation: 2,
  },
  siblingInputWrapperPicked: {
    borderColor: Colors.success,
    backgroundColor: '#F0FDF4',
  },
  siblingInputIcon: {
    marginRight: 8,
  },
  siblingInput: {
    flex: 1,
    fontSize: 14,
    color: '#0F172A',
    fontWeight: '500',
    borderWidth: 0,
    paddingVertical: 0,
    paddingHorizontal: 0,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web'
      ? ({
          outlineStyle: 'none',
          outlineWidth: 0,
          outline: 'none',
          boxShadow: 'none',
        } as any)
      : {}),
  },
  verifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DCFCE7',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
  },
  verifiedBadgeText: {
    fontSize: 11,
    fontWeight: '800',
    color: Colors.success,
  },
  suggestionList: {
    position: 'absolute',
    top: 68,
    left: 0,
    right: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 14,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 18,
    elevation: 12,
    zIndex: 99999,
    overflow: 'hidden',
    ...(Platform.OS === 'web' ? ({ zIndex: 99999 } as any) : {}),
  },
  suggestionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 10,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  suggestionRowDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
  },
  suggestionIconBox: {
    width: 28,
    height: 28,
    borderRadius: 8,
    backgroundColor: '#EFF6FF',
    justifyContent: 'center',
    alignItems: 'center',
  },
  suggestionName: {
    fontSize: 13,
    fontWeight: '700',
    color: '#0F172A',
  },
  suggestionClass: {
    fontSize: 11,
    fontWeight: '500',
    color: '#64748B',
  },
  suggestionStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
  },
  suggestionStatusText: {
    fontSize: 12,
    color: '#64748B',
    lineHeight: 16,
  },
  noticeBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#EFF6FF',
    borderWidth: 1,
    borderColor: '#BFDBFE',
    borderRadius: 14,
    padding: 12,
    position: 'relative',
    zIndex: 1,
  },
  noticeText: {
    flex: 1,
    fontSize: 12,
    color: '#1E40AF',
    lineHeight: 17,
  },

  /* PINNED STICKY FOOTER */
  pinnedFooter: {
    backgroundColor: '#FFFFFF',
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 18,
  },
  footerInner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  footerSummary: {
    flex: 1,
    minWidth: 180,
  },
  footerSummaryLabel: {
    fontSize: 10,
    fontWeight: '800',
    color: '#64748B',
    letterSpacing: 0.8,
  },
  footerSummaryPriceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: 4,
    marginTop: 2,
  },
  footerSummaryPlanName: {
    fontSize: 15,
    fontWeight: '800',
    color: '#0F172A',
  },
  footerSummaryDivider: {
    color: '#94A3B8',
    fontSize: 12,
  },
  footerSummaryAmount: {
    fontSize: 18,
    fontWeight: '900',
    color: Colors.primary,
  },
  footerSummaryUnit: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
  },
  footerButton: {
    minWidth: 180,
  },
  trustMarkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 10,
  },
  trustMarkText: {
    fontSize: 11,
    fontWeight: '500',
    color: '#94A3B8',
  },

  /* BUTTONS & STATES */
  primaryButton: {
    backgroundColor: Colors.primary,
    borderRadius: 14,
    paddingVertical: 13,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: Colors.primary,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 4,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : {}),
  },
  primaryButtonDisabled: {
    opacity: 0.55,
    shadowOpacity: 0,
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '700',
  },
  buttonTextRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonLoadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    gap: 12,
  },
  loadingText: {
    fontSize: 13,
    color: '#64748B',
    fontWeight: '500',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 36,
    paddingHorizontal: 20,
    gap: 8,
  },
  emptyIconCircle: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#FEF3C7',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 8,
  },
  emptyStateTitle: {
    fontSize: 16,
    fontWeight: '800',
    color: '#0F172A',
  },
  emptyStateText: {
    fontSize: 13,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 18,
    maxWidth: 360,
  },

  /* CONGRATULATIONS ONBOARDING SCREEN */
  congratsContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 36,
    paddingHorizontal: 28,
  },
  congratsBadgeGlow: {
    marginBottom: 16,
  },
  congratsIconWrapper: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#D1FAE5',
    borderWidth: 4,
    borderColor: '#ECFDF5',
    alignItems: 'center',
    justifyContent: 'center',
  },
  congratsPill: {
    backgroundColor: '#F0FDF4',
    borderWidth: 1,
    borderColor: '#BBF7D0',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 20,
    marginBottom: 12,
  },
  congratsPillText: {
    fontSize: 12,
    fontWeight: '800',
    color: '#15803D',
  },
  congratsTitle: {
    fontSize: 24,
    fontWeight: '900',
    color: '#0F172A',
    marginBottom: 8,
    textAlign: 'center',
    letterSpacing: -0.5,
  },
  congratsBody: {
    fontSize: 14,
    color: '#475569',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 22,
    maxWidth: 420,
  },
  congratsBodyBold: {
    fontWeight: '800',
    color: '#0F172A',
  },
  congratsSummaryCard: {
    width: '100%',
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
  },
  congratsSummaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  congratsSummaryCol: {
    flex: 1,
  },
  congratsSummaryLabel: {
    fontSize: 10,
    fontWeight: '800',
    color: '#64748B',
    letterSpacing: 0.8,
    marginBottom: 4,
  },
  congratsSummaryValue: {
    fontSize: 13,
    fontWeight: '800',
    color: '#0F172A',
  },
});
